import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { t } from '../../i18n';
import './departments.scss';

interface DepartmentDetailed {
    id: number;
    name: string;
    description: string | null;
    articles_count: number;
    users_count: number;
    admins_count: number;
    can_delete: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface Props {
    history: any;
    actions: any;
    adminActions: any;
    departmentsDetailed: DepartmentDetailed[];
    loading: boolean;
    buttonLoader: boolean;
}

const Departments = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<DepartmentDetailed | null>(null);

    useEffect(() => {
        props.adminActions.getAllDepartmentsDetailed();
    }, []);

    const refresh = () => props.adminActions.getAllDepartmentsDetailed();

    const list = props.departmentsDetailed || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(d => d.name.toLowerCase().includes(q));
    }, [list, search]);

    const isInitialLoad = props.loading && list.length === 0;

    const handleStartEdit = (dept: DepartmentDetailed) => {
        setEditingId(dept.id);
        setEditingName(dept.name);
    };

    const handleSaveEdit = (dept: DepartmentDetailed) => {
        const name = editingName.trim();
        if (!name) {
            toast.error(t('admin.departments.toastFillName'));
            return;
        }
        if (name === dept.name) {
            setEditingId(null);
            return;
        }
        props.adminActions.editDepartment(dept.id, { name }).then(() => {
            setEditingId(null);
            setEditingName('');
            refresh();
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleDelete = (dept: DepartmentDetailed) => {
        if (!dept.can_delete) {
            toast.error(t('admin.departments.toast.hasArticles'));
            return;
        }
        setDeleteConfirm(dept);
    };

    const confirmDelete = () => {
        if (!deleteConfirm) return;
        const id = deleteConfirm.id;
        props.adminActions.deleteDepartment(id).then(() => {
            setDeleteConfirm(null);
            refresh();
        });
    };

    const handleStartCreate = () => {
        setCreating(true);
        setNewName('');
    };

    const handleSaveCreate = () => {
        const name = newName.trim();
        if (!name) {
            toast.error(t('admin.departments.toastFillName'));
            return;
        }
        props.adminActions.createDepartment({ name }).then(() => {
            setCreating(false);
            setNewName('');
            refresh();
        });
    };

    const handleCancelCreate = () => {
        setCreating(false);
        setNewName('');
    };

    const formatDate = (iso: string | null) => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString(intl.locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const renderCheckIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );

    const renderCloseIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    return (
        <div className="dept-page">
            <MikaPageHeader
                title={<FormattedMessage id="admin.departments.pageTitle" defaultMessage="Departamentet" />}
                subtitle={fm('admin.departments.pageSubtitle', 'Menaxho repartet e prodhimit dhe punëtorët e secilit')}
                actions={
                    <button
                        type="button"
                        className="dept-btn dept-btn--primary"
                        onClick={handleStartCreate}
                        disabled={creating || props.buttonLoader}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5v14" />
                        </svg>
                        <FormattedMessage id="admin.departments.createBtn" defaultMessage="Krijo departament" />
                    </button>
                }
            />

            <div className="dept-toolbar">
                <div className="dept-search">
                    <svg className="dept-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        placeholder={fm('admin.departments.searchPlaceholder', 'Kërko departament...')}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="dept-toolbar__count">
                    <FormattedMessage
                        id="admin.departments.countTotal"
                        defaultMessage="{count, plural, one {# departament} other {# departamente}}"
                        values={{ count: filtered.length }}
                    />
                </div>
            </div>

            <div className="dept-list">
                <div className="dept-list__header">
                    <div className="dept-list__col dept-list__col--name">
                        <FormattedMessage id="admin.departments.col.name" defaultMessage="Emri" />
                    </div>
                    <div className="dept-list__col dept-list__col--num">
                        <FormattedMessage id="admin.departments.col.articles" defaultMessage="Artikuj" />
                    </div>
                    <div className="dept-list__col dept-list__col--num">
                        <FormattedMessage id="admin.departments.col.users" defaultMessage="Punëtorë" />
                    </div>
                    <div className="dept-list__col dept-list__col--num">
                        <FormattedMessage id="admin.departments.col.admins" defaultMessage="Admin" />
                    </div>
                    <div className="dept-list__col dept-list__col--date">
                        <FormattedMessage id="admin.departments.col.created" defaultMessage="Krijuar" />
                    </div>
                    <div className="dept-list__col dept-list__col--actions" />
                </div>

                {isInitialLoad ? (
                    [0, 1, 2, 3].map(i => (
                        <div key={`sk-${i}`} className="dept-row dept-row--skeleton">
                            <div className="dept-row__cell dept-row__cell--name">
                                <div className="dept-skeleton dept-skeleton--icon" />
                                <div className="dept-skeleton dept-skeleton--name" />
                            </div>
                            <div className="dept-row__cell dept-row__cell--num"><div className="dept-skeleton dept-skeleton--num" /></div>
                            <div className="dept-row__cell dept-row__cell--num"><div className="dept-skeleton dept-skeleton--num" /></div>
                            <div className="dept-row__cell dept-row__cell--num"><div className="dept-skeleton dept-skeleton--num" /></div>
                            <div className="dept-row__cell dept-row__cell--date"><div className="dept-skeleton dept-skeleton--date" /></div>
                            <div className="dept-row__cell dept-row__cell--actions" />
                        </div>
                    ))
                ) : (
                    <>
                        {filtered.length === 0 && !creating && (
                            <div className="dept-empty">
                                {search.trim() ? (
                                    <FormattedMessage id="admin.departments.empty.search" defaultMessage="Asnjë departament nuk përputhet me kërkimin." />
                                ) : (
                                    <FormattedMessage id="admin.departments.empty.list" defaultMessage="Nuk ka departamente. Krijoni një të ri për të filluar." />
                                )}
                            </div>
                        )}

                        {filtered.map(dept => {
                            const isEditing = editingId === dept.id;
                            const initial = (dept.name?.charAt(0) || '?').toUpperCase();

                            return (
                                <div
                                    key={dept.id}
                                    className={`dept-row ${isEditing ? 'dept-row--editing' : ''}`}
                                >
                                    <div className="dept-row__cell dept-row__cell--name">
                                        <div className="dept-row__icon">{initial}</div>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                className="dept-row__edit-input"
                                                value={editingName}
                                                onChange={e => setEditingName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSaveEdit(dept);
                                                    }
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        handleCancelEdit();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            <div className="dept-row__name">{dept.name}</div>
                                        )}
                                    </div>
                                    <div className="dept-row__cell dept-row__cell--num">{dept.articles_count}</div>
                                    <div className="dept-row__cell dept-row__cell--num">{dept.users_count}</div>
                                    <div className="dept-row__cell dept-row__cell--num">{dept.admins_count}</div>
                                    <div className="dept-row__cell dept-row__cell--date">{formatDate(dept.created_at)}</div>
                                    <div className="dept-row__cell dept-row__cell--actions">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="dept-icon-btn dept-icon-btn--success"
                                                    onClick={() => handleSaveEdit(dept)}
                                                    title={fm('common.action.save', 'Ruaj')}
                                                    disabled={props.buttonLoader}
                                                >
                                                    {renderCheckIcon}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="dept-icon-btn"
                                                    onClick={handleCancelEdit}
                                                    title={fm('common.action.cancel', 'Anulo')}
                                                >
                                                    {renderCloseIcon}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="dept-icon-btn"
                                                    onClick={() => handleStartEdit(dept)}
                                                    title={fm('common.action.edit', 'Ndrysho')}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="dept-icon-btn dept-icon-btn--danger"
                                                    onClick={() => handleDelete(dept)}
                                                    title={dept.can_delete ? fm('common.action.delete', 'Fshi') : fm('admin.departments.toast.hasArticles', 'Nuk mund ta fshish — ka artikuj të lidhur')}
                                                    disabled={!dept.can_delete}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {creating ? (
                            <div className="dept-add-row dept-add-row--editing">
                                <div className="dept-row__cell dept-row__cell--name">
                                    <div className="dept-row__icon dept-row__icon--add">+</div>
                                    <input
                                        type="text"
                                        className="dept-row__edit-input"
                                        value={newName}
                                        placeholder={fm('admin.departments.placeholderName', 'Emri i departamentit...')}
                                        onChange={e => setNewName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSaveCreate();
                                            }
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                handleCancelCreate();
                                            }
                                        }}
                                        autoFocus
                                    />
                                </div>
                                <div className="dept-row__cell dept-row__cell--actions">
                                    <button
                                        type="button"
                                        className="dept-icon-btn dept-icon-btn--success"
                                        onClick={handleSaveCreate}
                                        title={fm('common.action.create', 'Krijo')}
                                        disabled={props.buttonLoader}
                                    >
                                        {renderCheckIcon}
                                    </button>
                                    <button
                                        type="button"
                                        className="dept-icon-btn"
                                        onClick={handleCancelCreate}
                                        title={fm('common.action.cancel', 'Anulo')}
                                    >
                                        {renderCloseIcon}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="dept-add-row" onClick={handleStartCreate}>
                                <div className="dept-row__icon dept-row__icon--add">+</div>
                                <div className="dept-add-row__label">
                                    <FormattedMessage id="admin.departments.addNew" defaultMessage="Krijo departament të ri" />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <MikaConfirmModal
                isOpen={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={confirmDelete}
                title={fm('admin.departments.deleteTitle', 'Fshi departamentin')}
                message={intl.formatMessage(
                    { id: 'admin.departments.deleteConfirm', defaultMessage: 'A jeni të sigurt që doni të fshini {name}?' },
                    { name: '"' + (deleteConfirm?.name || '') + '"' }
                )}
                confirmLabel={fm('common.action.delete', 'Fshi')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        loading: state.app.loading,
        buttonLoader: state.app.buttonLoader,
        departmentsDetailed: (state.admin as any).departmentsDetailed || [],
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(Departments as any));
